/* ============================================================
   KYRO PDP — Shopify Impact-compatible interactions
   - Inline SVG icons (no Lucide dependency)
   - Variant + selling plan picker integrated with Shopify form
   - AJAX add-to-cart via /cart/add.js, then triggers Impact's
     cart drawer ('cart:open' custom event) or falls back to /cart
   - Gallery + reveal + accordions + sticky bar + marquee

   FIXED 2026-10-05:
   1. Shopify web pixel event used window.analytics, which is undefined on
      this store. Correct global is Shopify.analytics. AddToCart had never
      fired to Meta as a result.
   2. Added a direct fbq('track','AddToCart') call as a deterministic
      fallback, since Shopify.analytics.publish is not guaranteed to map a
      standard event name through to the web pixel bridge.
   3. Purchase mode (one-time vs subscribe) is now read from the DOM on init
      so state.sellingPlanId always matches whichever .pmode has .active in
      the Liquid. Previously state assumed one-time regardless of markup.
   ============================================================ */
(function () {
  'use strict';

  function $(s, c) { return (c || document).querySelector(s); }
  function $all(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }

  /* ---------- Inline icon registry ----------
     Replaces Lucide. data-icon="name" → injects SVG. */
  var ICONS = {
    leaf: '<path d="M11 20A7 7 0 0 1 4 13c0-7 8-9 16-9 0 8-2 16-9 16z"/><path d="M2 22c5-5 8-9 8-13"/>',
    'chevron-right': '<polyline points="9 18 15 12 9 6"/>',
    'chevron-left': '<polyline points="15 18 9 12 15 6"/>',
    minus: '<line x1="5" y1="12" x2="19" y2="12"/>',
    plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
    truck: '<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
    'rotate-ccw': '<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>',
    gift: '<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
    'shield-check': '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>',
    star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    check: '<polyline points="20 6 9 17 4 12"/>',
    x: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
    'flask-conical': '<path d="M9 2v6L4 18a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3L15 8V2"/><line x1="7" y1="2" x2="17" y2="2"/>',
    'qr-code': '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><line x1="14" y1="14" x2="14" y2="21"/><line x1="18" y1="14" x2="18" y2="17"/><line x1="14" y1="18" x2="17" y2="18"/><line x1="20" y1="18" x2="21" y2="18"/>',
    'map-pin': '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
    'badge-check': '<polygon points="12 2 15 5 19 5 19 9 22 12 19 15 19 19 15 19 12 22 9 19 5 19 5 15 2 12 5 9 5 5 9 5 12 2"/><polyline points="9 12 11 14 15 10"/>',
    'arrow-right': '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>'
  };
  function renderIcons(root) {
    $all('[data-icon]', root || document).forEach(function (el) {
      if (el.dataset.iconDone) return;
      var name = el.getAttribute('data-icon');
      var inner = ICONS[name] || '';
      el.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + inner + '</svg>';
      el.dataset.iconDone = '1';
    });
  }

  /* ---------- Open Impact's native cart drawer.
     Impact uses Archetype's web-component pattern:
       <cart-drawer>   ← drawer container
       <cart-count>    ← badge in header
       <a aria-controls="cart-drawer">  ← cart icon link
     Strategy:
       1. Re-fetch /cart.js + the cart-drawer section in parallel.
       2. Swap the cart-drawer section HTML with the fresh markup.
       3. Update every <cart-count> badge directly (failsafe).
       4. Dispatch cart:updated (the standard Archetype event).
       5. Open the drawer — call cart-drawer.open() if present, otherwise
          click the header cart link (which Impact intercepts to open). ---------- */
  function openImpactCartDrawer() {
    /* Re-fetch the cart so listeners (badge updaters, Klaviyo, etc.) have the current state. */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {

        /* 1. Tell Impact the cart changed.
              <cart-count> caches the cart in a module-level promise that only
              cart:change refreshes. Without this, the cart:refresh below reads
              the stale page-load value and overwrites the new count — which is
              why the badge only caught up on a page load.
              We deliberately do NOT write textContent on <cart-count> first:
              its setter early-returns when the number already matches, which
              skips the fade-in and leaves the badge at opacity 0. */
        if (cart) {
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));

          /* Non-Impact badges still need writing by hand. */
          $all('.count-bubble, [data-cart-count]').forEach(function (el) {
            if (el.tagName === 'CART-COUNT') return;
            el.textContent = cart.item_count;
            if (cart.item_count > 0) {
              el.removeAttribute('hidden');
              el.setAttribute('aria-hidden', 'false');
            }
          });

          /* Belt and braces on the opacity utility, in case the badge rendered
             while the cart was empty and the fade never ran. */
          $all('cart-count').forEach(function (el) {
            el.classList.toggle('opacity-0', cart.item_count === 0);
          });
        }

        /* 2. Dispatch the standard Archetype events so Impact's <cart-drawer> web
              component re-renders itself with all upsell ("Add to your stack")
              bindings intact. Do NOT swap the section HTML manually — doing so
              strips the click handlers Impact attaches at page load. */
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));

        /* 3. Open the drawer. */
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else {
          var cartLink = document.querySelector(
            'a[aria-controls="cart-drawer"], a[href="/cart"][aria-controls], a.header__icon--cart, [data-drawer-trigger="cart"]'
          );
          if (cartLink) cartLink.click();
        }
      });
  }

  /* ---------- Format money ---------- */
  function money(cents) {
    if (typeof cents !== 'number') return '';
    var d = (cents / 100);
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  /* ---------- Render every icon on the page FIRST, independent of section init.
     This guarantees icons appear even if a per-section initPDP call throws. ---------- */
  function renderAllIcons() { renderIcons(document); }
  renderAllIcons();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderAllIcons);

  /* ---------- Clone Judge.me's Write Review button into the KYRO summary block.
     JM's V3 widget loads async, so we observe the DOM until the button shows up,
     then create a styled KYRO clone in [data-rv-write-slot]. Click on the clone
     triggers .click() on the (hidden) original so JM's modal still opens. ---------- */
  function relocateJudgemeWriteButton() {
    var slot = document.querySelector('.kyro-pdp [data-rv-write-slot]');
    if (!slot || slot.dataset.populated) return false;
    var jmBtn = document.querySelector('.kyro-pdp .jm-action-buttons__button, .kyro-pdp [data-testid="write-review-button"]');
    if (!jmBtn) return false;
    var clone = document.createElement('button');
    clone.type = 'button';
    clone.className = 'rv-write-btn';
    clone.textContent = (jmBtn.textContent || '').trim() || 'Write a review';
    clone.addEventListener('click', function () { jmBtn.click(); });
    slot.appendChild(clone);
    slot.dataset.populated = '1';
    return true;
  }

  /* ---------- Sync KYRO histogram bars with Judge.me's live data.
     Primary path: read from window.jdgm.data.reviewWidget[productId] which
     kyro-reviews.liquid populates server-side from the JM metafield.
     Fallback: scrape the rendered widget DOM. Silently leaves manual
     section settings in place if neither path yields data. ---------- */
  function getJudgemeCountsByStar() {
    // 1) Try server-populated data object (most reliable, no DOM dependency)
    try {
      var jmRoot = document.querySelector('.kyro-pdp [data-product-id], .kyro-pdp #judgeme_product_reviews');
      var pid = jmRoot && (jmRoot.getAttribute('data-product-id') || jmRoot.getAttribute('data-id'));
      if (pid && window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget) {
        var d = window.jdgm.data.reviewWidget[pid] || window.jdgm.data.reviewWidget[parseInt(pid, 10)];
        if (d) {
          // JM ships this in a few shapes across versions — try each
          var dist = d.rating_distribution || d.ratings_distribution || d.histogram || d.distribution;
          if (dist) {
            var counts = {};
            // Shape A: object keyed by star {5: 12, 4: 1, ...}
            if (!Array.isArray(dist) && typeof dist === 'object') {
              for (var k = 1; k <= 5; k++) counts[k] = parseInt(dist[k] || dist[String(k)] || 0, 10);
              return counts;
            }
            // Shape B: array of {rating, count} objects
            if (Array.isArray(dist)) {
              dist.forEach(function (row) {
                var s = parseInt(row.rating || row.star || row[0], 10);
                var c = parseInt(row.count || row.frequency_count || row[1] || 0, 10);
                if (s >= 1 && s <= 5) counts[s] = c;
              });
              if (Object.keys(counts).length) return counts;
            }
          }
        }
      }
    } catch (e) { /* swallow and fall through to DOM */ }

    // 2) Fallback: scrape DOM (widest selector net)
    var rows = document.querySelectorAll(
      '.kyro-pdp .jdgm-histogram-row, .kyro-pdp .jdgm-histo__row, ' +
      '.kyro-pdp [class*="histogram-row"], .kyro-pdp [class*="histo__row"], ' +
      '.kyro-pdp [class*="histogram"] [data-rating]'
    );
    if (!rows.length) return null;
    var domCounts = {};
    rows.forEach(function (row) {
      var s = parseInt(row.getAttribute('data-rating') || (row.dataset && row.dataset.rating), 10);
      if (!s) {
        var labelEl = row.querySelector('[class*="rating-text"], [class*="label"], span');
        if (labelEl) s = parseInt((labelEl.textContent || '').trim(), 10);
      }
      if (!s || s < 1 || s > 5) return;
      // Try data-count or text content of a count/frequency cell
      var c = parseInt(row.getAttribute('data-count') || (row.dataset && row.dataset.count), 10);
      if (isNaN(c)) {
        var countEl = row.querySelector('[class*="frequency"], [class*="count"]');
        if (countEl) c = parseInt((countEl.textContent || '').replace(/\D/g, ''), 10);
      }
      if (!isNaN(c)) domCounts[s] = c;
    });
    return Object.keys(domCounts).length ? domCounts : null;
  }

  function syncJudgemeHistogram() {
    var bars = document.querySelectorAll('.kyro-pdp .rv-bars .rv-bar');
    if (!bars.length || bars[0].dataset.synced === '1') return false;

    var counts = getJudgemeCountsByStar();
    if (!counts) return false;

    var total = 0;
    for (var s = 1; s <= 5; s++) total += (counts[s] || 0);
    if (total <= 0) return false;

    var pctByStar = {};
    for (var s2 = 1; s2 <= 5; s2++) pctByStar[s2] = Math.round(((counts[s2] || 0) / total) * 100);

    bars.forEach(function (bar) {
      var labelEl = bar.querySelector('span:first-child');
      if (!labelEl) return;
      var starN = parseInt((labelEl.textContent || '').trim(), 10);
      if (!starN || pctByStar[starN] === undefined) return;
      var pct = pctByStar[starN];
      var fillEl = bar.querySelector('.fill');
      var labelOut = bar.querySelector('span:last-child');
      if (fillEl) fillEl.style.width = pct + '%';
      if (labelOut) labelOut.textContent = pct + '%';
    });
    bars[0].dataset.synced = '1';
    return true;
  }

  // Try immediately, then watch for JM's widget rendering
  var jmButtonDone = relocateJudgemeWriteButton();
  var jmHistoDone  = syncJudgemeHistogram();
  if (!jmButtonDone || !jmHistoDone) {
    var jmObserver = new MutationObserver(function () {
      if (!jmButtonDone) jmButtonDone = relocateJudgemeWriteButton();
      if (!jmHistoDone)  jmHistoDone  = syncJudgemeHistogram();
      if (jmButtonDone && jmHistoDone) jmObserver.disconnect();
    });
    jmObserver.observe(document.body, { childList: true, subtree: true });
    // Safety: give up after 10s
    setTimeout(function () { jmObserver.disconnect(); }, 10000);
  }

  /* ---------- For each PDP root on the page ---------- */
  $all('.kyro-pdp[data-pdp-root]').forEach(function (root) {
    try { initPDP(root); }
    catch (e) { console.warn('KYRO PDP: section init failed', e); }
  });

  function initPDP(root) {
    renderIcons(root);

    /* If this wrapper is NOT the main product section, run universal hooks then exit.
       Without this guard, JSON.parse(null.textContent) throws and forEach aborts —
       which leaves all reveal-animated content stuck at opacity:0. */
    var productJsonEl = root.querySelector('[data-product-json]');
    if (!productJsonEl) {
      $all('[data-acc-head]', root).forEach(function (h) {
        h.addEventListener('click', function () { h.parentElement.classList.toggle('open'); });
      });
      $all('[data-faq-q]', root).forEach(function (q) {
        q.addEventListener('click', function () { q.parentElement.classList.toggle('open'); });
      });
      /* Marquee duplication now happens in Liquid for reliability — JS no-op. */
      var reveals0 = $all('.reveal', root);
      if ('IntersectionObserver' in window) {
        var io0 = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { e.target.classList.add('in'); io0.unobserve(e.target); }
          });
        }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
        reveals0.forEach(function (el) { io0.observe(el); });
      } else {
        reveals0.forEach(function (el) { el.classList.add('in'); });
      }
      /* Sticky buy and closing CTA need to fire the main product form even though
         their wrappers have no product JSON. We wire them to the global form here. */
      var globalForm = document.querySelector('form[data-product-form], form.kyro-product-form, form#kyro-product-form');
      var sbAtc0 = root.querySelector('[data-sb-atc]');
      if (sbAtc0 && globalForm) sbAtc0.addEventListener('click', function () { globalForm.requestSubmit ? globalForm.requestSubmit() : globalForm.dispatchEvent(new Event('submit', { cancelable: true })); });
      var ctaBuy0 = root.querySelector('[data-cta-buy]');
      if (ctaBuy0 && globalForm) ctaBuy0.addEventListener('click', function () { globalForm.requestSubmit ? globalForm.requestSubmit() : globalForm.dispatchEvent(new Event('submit', { cancelable: true })); });
      /* Sticky buy visibility — watch for the main buy box anywhere on the page */
      var stickyBuy0 = root.querySelector('[data-sticky-buy]');
      if (stickyBuy0) {
        var buyboxGlobal = document.querySelector('.kyro-pdp .buybox');
        var stickyVis0 = function () {
          if (!buyboxGlobal) return;
          var r = buyboxGlobal.getBoundingClientRect();
          var show = r.bottom < 120 && (window.innerHeight - r.bottom) > 100;
          stickyBuy0.classList.toggle('show', show);
        };
        window.addEventListener('scroll', stickyVis0, { passive: true });
        stickyVis0();
      }
      return;
    }

    var product;
    try { product = JSON.parse(productJsonEl.textContent); }
    catch (e) { console.warn('KYRO PDP: invalid product JSON', e); return; }

    var state = {
      variantId: product.selected_or_first_available_variant_id || (product.variants[0] && product.variants[0].id),
      sellingPlanId: null, // null = one-time. Re-synced from the DOM in INIT below.
      qty: 1
    };
    var variantMap = {};
    product.variants.forEach(function (v) { variantMap[v.id] = v; });

    /* ---- GALLERY ---- */
    var slides = $all('[data-slide]', root);
    var thumbs = $all('[data-thumb]', root);
    var gi = 0;
    function showSlide(i) {
      if (!slides.length) return;
      gi = ((i % slides.length) + slides.length) % slides.length;
      slides.forEach(function (s, k) { s.classList.toggle('active', k === gi); });
      thumbs.forEach(function (t) { t.classList.toggle('active', parseInt(t.getAttribute('data-thumb'), 10) === gi); });
    }
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showSlide(parseInt(t.getAttribute('data-thumb'), 10)); });
    });
    var prev = root.querySelector('[data-gallery-prev]');
    var next = root.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { showSlide(gi - 1); });
    if (next) next.addEventListener('click', function () { showSlide(gi + 1); });

    /* ---- VARIANT SWATCHES ---- */
    function applySwatchColorToTitle(swatchBtn) {
      if (!swatchBtn) return;
      var dab = swatchBtn.querySelector('.dab');
      var c = dab ? dab.style.background : '';
      $all('.buybox h1 .flav', root).forEach(function (el) { el.style.color = c || ''; });
    }

    function refreshSubscribePmodeForVariant() {
      var subBtn = root.querySelector('[data-pmode][data-selling-plan]:not([data-selling-plan=""])');
      if (!subBtn) return;
      var v = variantMap[state.variantId];
      if (!v) return;
      var allocs = v.selling_plan_allocations || [];
      if (!allocs.length) {
        // Active variant has no subscription. Force one-time and hide subscribe.
        var oneTime = root.querySelector('[data-pmode][data-selling-plan=""]');
        if (oneTime) { oneTime.classList.add('active'); subBtn.classList.remove('active'); }
        subBtn.style.display = 'none';
        state.sellingPlanId = null;
        var spInputHidden = root.querySelector('[name="selling_plan"]');
        if (spInputHidden) spInputHidden.value = '';
        return;
      }
      subBtn.style.display = '';
      var alloc = allocs[0];
      var newSpId = String(alloc.selling_plan_id);
      var disc = v.price > 0 ? (v.price - alloc.per_delivery_price) / v.price : 0;
      subBtn.setAttribute('data-selling-plan', newSpId);
      subBtn.setAttribute('data-discount', String(disc));
      // If subscribe was already selected, propagate the new ID into the form.
      if (subBtn.classList.contains('active')) {
        state.sellingPlanId = newSpId;
        var spInput = root.querySelector('[name="selling_plan"]');
        if (spInput) spInput.value = newSpId;
      }
    }

    /* ---- Read whichever purchase mode the Liquid marked .active and make
       state + the hidden selling_plan input agree with it. Without this, the
       markup and the JS can disagree on page load (e.g. Liquid pre-selects
       one-time but state still holds a selling plan, or vice versa). ---- */
    function syncPmodeFromDom() {
      var active = root.querySelector('[data-pmode].active');
      var spInput = root.querySelector('[name="selling_plan"]');
      if (!active) {
        state.sellingPlanId = null;
        if (spInput) spInput.value = '';
        return;
      }
      var sp = active.getAttribute('data-selling-plan');
      state.sellingPlanId = sp && sp !== '' ? sp : null;
      if (spInput) spInput.value = state.sellingPlanId || '';
    }

    $all('[data-swatch]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.classList.contains('soldout')) return;
        $all('[data-swatch]', root).forEach(function (x) { x.classList.remove('active'); });
        b.classList.add('active');
        var vid = parseInt(b.getAttribute('data-variant-id'), 10);
        var img = b.getAttribute('data-img');
        var label = b.getAttribute('data-label');
        state.variantId = vid;
        var hidden = root.querySelector('[name="id"]');
        if (hidden) hidden.value = vid;
        refreshSubscribePmodeForVariant();
        // update flavour labels
        $all('[data-flav-label]', root).forEach(function (el) { el.textContent = label; });
        // tint the title flav to match the active swatch
        applySwatchColorToTitle(b);
        // swap hero image if provided
        if (img) {
          var hero = root.querySelector('[data-hero-img]');
          if (hero) {
            hero.src = img;
            /* The hero now ships a srcset. Clear it on variant swap so the
               browser uses the new src rather than a cached candidate from
               the previous variant. */
            if (hero.hasAttribute('srcset')) hero.removeAttribute('srcset');
          }
          var t0 = root.querySelector('[data-thumb="0"] img');
          if (t0) t0.src = img;
          var sbImg = root.querySelector('[data-sb-img]');
          if (sbImg) sbImg.src = img;
        }
        updatePrice();
      });
    });
    // init: tint title flav from the swatch that's active on page load
    applySwatchColorToTitle(root.querySelector('[data-swatch].active'));

    /* ---- PURCHASE MODE (selling plan) ---- */
    $all('[data-pmode]', root).forEach(function (m) {
      m.addEventListener('click', function () {
        $all('[data-pmode]', root).forEach(function (x) { x.classList.remove('active'); });
        m.classList.add('active');
        var sp = m.getAttribute('data-selling-plan');
        state.sellingPlanId = sp && sp !== '' ? sp : null;
        var spInput = root.querySelector('[name="selling_plan"]');
        if (spInput) spInput.value = state.sellingPlanId || '';
        updatePrice();
      });
    });

    /* ---- QTY ---- */
    var qtyInput = root.querySelector('[data-qty]');
    function setQty(q) {
      q = Math.max(1, Math.min(20, q || 1));
      state.qty = q;
      if (qtyInput) qtyInput.value = q;
      updatePrice();
    }
    var qMinus = root.querySelector('[data-qty-minus]');
    var qPlus = root.querySelector('[data-qty-plus]');
    if (qMinus) qMinus.addEventListener('click', function () { setQty(state.qty - 1); });
    if (qPlus) qPlus.addEventListener('click', function () { setQty(state.qty + 1); });
    if (qtyInput) qtyInput.addEventListener('input', function () { setQty(parseInt(qtyInput.value, 10)); });
    if (qtyInput) qtyInput.addEventListener('change', function () { setQty(parseInt(qtyInput.value, 10)); });

    /* Pull in a quantity written straight to the input by a third-party widget.
       Assigning input.value fires neither 'input' nor 'change', so without this
       state.qty stays stale and the submit handler posts the wrong number. */
    function syncQtyFromDom() {
      if (!qtyInput) return;
      var domQty = parseInt(qtyInput.value, 10);
      if (domQty && domQty !== state.qty) setQty(domQty);
    }

    /* ---- APPSTLE VOLUME DISCOUNT BRIDGE ----
       Appstle's volume-discount widget renders its own tier cards and never
       touches the theme quantity input. Verified on the live Creatine PDP:
       clicking "800g Creatine" selects the card but leaves quantity at 1, so a
       customer choosing four tubs receives one.

       Each card prints its compare-at total ($79.96 for the 800g tier). Divided
       by the unit price that gives the tier quantity, which holds regardless of
       how the tiers are named or reordered in the Appstle dashboard. */
    function appstleTierQty(card) {
      var v = variantMap[state.variantId];
      var unit = v && v.price;
      if (!unit) return 0;
      var prices = (card.innerText || '').match(/\$\s?\d[\d,]*\.?\d*/g);
      if (!prices) return 0;
      var top = prices.reduce(function (max, p) {
        var cents = Math.round(parseFloat(p.replace(/[$,\s]/g, '')) * 100);
        return cents > max ? cents : max;
      }, 0);
      var q = Math.round(top / unit);
      return q >= 1 && q <= 20 ? q : 0;
    }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var widget = e.target.closest('.appstle-volume-discount');
      if (!widget) return;
      var grid = widget.querySelector('.ab-volume-discount-rule-content-v2');
      if (!grid) return;
      var card = null;
      [].forEach.call(grid.children, function (c) { if (c.contains(e.target)) card = c; });
      if (!card) return;
      /* Let Appstle finish its own re-render before writing the quantity. */
      setTimeout(function () {
        var q = appstleTierQty(card);
        if (q) setQty(q);
      }, 80);
    }, true);

    /* ---- PRICE DISPLAY ---- */
    function currentVariantPriceCents() {
      var v = variantMap[state.variantId];
      if (!v) return 0;
      // Subscription discount: read from data-discount on active pmode (decimal, e.g. 0.10)
      var active = root.querySelector('[data-pmode].active');
      var disc = active ? parseFloat(active.getAttribute('data-discount') || '0') : 0;
      var price = v.price;
      if (state.sellingPlanId && disc > 0) {
        price = Math.round(price * (1 - disc));
      }
      return price;
    }
    function updatePrice() {
      var unit = currentVariantPriceCents();
      var line = unit * state.qty;
      var pn = root.querySelector('[data-price-now]');
      if (pn) pn.textContent = money(unit);
      var atcPrice = root.querySelector('[data-atc-price]');
      if (atcPrice) atcPrice.textContent = '· ' + money(line);
      var sbPrice = root.querySelector('[data-sb-price]');
      if (sbPrice) sbPrice.textContent = money(line);
      var ap = root.querySelector('afterpay-placement');
      if (ap) ap.setAttribute('data-amount', (unit / 100).toFixed(2));
      var pu = root.querySelector('[data-price-unit-template]');
      if (pu) {
        var servings = parseInt(pu.getAttribute('data-servings') || '0', 10);
        var sizeLabel = pu.getAttribute('data-size') || '';
        if (servings > 0) {
          var perServe = unit / servings;
          pu.textContent = sizeLabel + ' · ' + servings + ' servings · ' + money(perServe) + ' per serve' + (state.sellingPlanId ? ' (subscriber)' : '');
        }
      }
      // update pmode price labels live
      $all('[data-pmode]', root).forEach(function (m) {
        var disc = parseFloat(m.getAttribute('data-discount') || '0');
        var v = variantMap[state.variantId];
        if (!v) return;
        var p = disc > 0 ? Math.round(v.price * (1 - disc)) : v.price;
        var b = m.querySelector('[data-pmode-price]');
        if (b) b.textContent = money(p);
      });
    }

    /* ---- ACCORDIONS ---- */
    $all('[data-acc-head]', root).forEach(function (h) {
      h.addEventListener('click', function () { h.parentElement.classList.toggle('open'); });
    });

    /* ---- FAQ ---- */
    $all('[data-faq-q]', root).forEach(function (q) {
      q.addEventListener('click', function () { q.parentElement.classList.toggle('open'); });
    });

    /* ---- STOCK MESSAGE UTILITY ---- */
    var stockMsg = root.querySelector('[data-stock-message]');
    function showStockMessage(text, kind) {
      if (!stockMsg) return;
      stockMsg.textContent = text;
      stockMsg.className = 'stock-message ' + (kind || 'info');
      stockMsg.hidden = false;
    }
    function clearStockMessage() {
      if (!stockMsg) return;
      stockMsg.hidden = true;
      stockMsg.textContent = '';
    }

    /* ---- STOCK CHECK — returns the qty that should actually be added.
       Honors Shopify's inventory_management / inventory_policy fields:
       - If not tracked OR set to "continue" selling out-of-stock, no cap.
       - If tracked AND policy is "deny", cap at remaining inventory. ---- */
    function capQtyAgainstStock(requestedQty) {
      var v = variantMap[state.variantId];
      if (!v) return { qty: requestedQty, capped: false, available: requestedQty };
      var tracked = v.inventory_management === 'shopify';
      var deniesOversell = v.inventory_policy === 'deny';
      if (!tracked || !deniesOversell) {
        return { qty: requestedQty, capped: false, available: Infinity };
      }
      var stock = Math.max(0, v.inventory_quantity || 0);
      if (stock >= requestedQty) {
        return { qty: requestedQty, capped: false, available: stock };
      }
      return { qty: stock, capped: true, available: stock };
    }

    /* ---- AJAX ADD TO CART ---- */
    var form = root.querySelector('form[data-product-form], form.kyro-product-form, form#kyro-product-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        clearStockMessage();

        /* Trust the input over our cached state — a bundle app may have written
           to it since the last user interaction. */
        syncQtyFromDom();

        /* Pre-flight stock check */
        var check = capQtyAgainstStock(state.qty);
        if (check.available === 0) {
          showStockMessage('Sorry — this flavour is out of stock. Try another or check back soon.', 'error');
          return;
        }
        var qtyToAdd = check.qty;
        if (check.capped) {
          showStockMessage('Only ' + check.available + ' left — adding ' + check.available + ' to your cart.', 'warn');
        }

        var formData = new FormData(form);
        formData.set('quantity', String(qtyToAdd));
        // Force our selling_plan over anything injected by subscription apps (Appstle, etc.).
        // If state.sellingPlanId is null (one-time purchase), delete the field entirely
        // — sending selling_plan="" sometimes confuses Shopify's selling-plan validator.
        if (state.sellingPlanId) {
          formData.set('selling_plan', String(state.sellingPlanId));
        } else {
          formData.delete('selling_plan');
        }
        var atc = root.querySelector('[data-atc]');
        var atcLabel = atc && atc.querySelector('[data-atc-label]');
        var atcPrice = atc && atc.querySelector('[data-atc-price]');
        var prevLabel = atcLabel ? atcLabel.textContent : '';
        function setLoading(on) {
          if (!atc) return;
          atc.disabled = on;
          atc.classList.toggle('is-loading', on);
          if (atcPrice) atcPrice.style.visibility = on ? 'hidden' : '';
          if (atcLabel) atcLabel.textContent = on ? 'Adding…' : prevLabel;
        }
        setLoading(true);

        fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
          .then(function (res) {
            if (!res.ok) {
              setLoading(false);
              /* Shopify returns 422 when inventory rejects; show a friendly msg */
              var msg = res.data && res.data.description ? res.data.description : 'Could not add to cart.';
              if (res.status === 422) {
                msg = 'Stock changed while you were on the page — please reduce the quantity or try a different flavour.';
              }
              showStockMessage(msg, 'error');
              return;
            }
            /* Successful add — flip to a brief "Added ✓" state, fire events, open drawer */
            if (atcLabel) atcLabel.textContent = 'Added ✓';
            if (atc) atc.classList.remove('is-loading');
            setTimeout(function () { setLoading(false); }, 1200);
            fireCartEvents(res.data);
            openImpactCartDrawer();
          })
          .catch(function () {
            setLoading(false);
            showStockMessage('Network hiccup — try once more.', 'error');
          });
      });
    }

    /* ---- FIRE EVENTS for Klaviyo, Shopify Web Pixels, Meta, generic apps ---- */
    function fireCartEvents(addedItem) {
      /* addedItem is the response from /cart/add.js — Shopify's standard line item object */

      /* Fetch the current cart for full payloads (Klaviyo wants ItemNames, etc.) */
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          var detail = { item: addedItem, cart: cart };

          /* 1. KLAVIYO — uses 'Added to Cart' event name with their canonical schema.
             Pushes to both legacy (_learnq) and modern (klaviyo) SDK so installs of either version pick it up. */
          if (addedItem && cart) {
            var klaviyoData = {
              '$value': cart.total_price / 100,
              AddedItemProductName: addedItem.product_title,
              AddedItemProductID: String(addedItem.product_id),
              AddedItemSKU: addedItem.sku || '',
              AddedItemCategories: addedItem.product_type ? [addedItem.product_type] : [],
              AddedItemImageURL: addedItem.image || '',
              AddedItemURL: window.location.origin + (addedItem.url || ''),
              AddedItemPrice: addedItem.final_price / 100,
              AddedItemQuantity: addedItem.quantity,
              ItemNames: cart.items.map(function (i) { return i.product_title; }),
              CheckoutURL: window.location.origin + '/checkout',
              Items: cart.items.map(function (i) {
                return {
                  ProductID: String(i.product_id),
                  SKU: i.sku || '',
                  ProductName: i.product_title,
                  Quantity: i.quantity,
                  ItemPrice: i.final_price / 100,
                  RowTotal: i.final_line_price / 100,
                  ProductURL: window.location.origin + i.url,
                  ImageURL: i.image || '',
                  ProductCategories: i.product_type ? [i.product_type] : []
                };
              })
            };
            try {
              window._learnq = window._learnq || [];
              window._learnq.push(['track', 'Added to Cart', klaviyoData]);
              if (window.klaviyo && typeof window.klaviyo.push === 'function') {
                window.klaviyo.push(['track', 'Added to Cart', klaviyoData]);
              }
            } catch (e) { console.warn('KYRO PDP: Klaviyo event failed', e); }
          }

          /* 2a. SHOPIFY WEB PIXELS — standard 'product_added_to_cart' event.
             FIXED: the global is Shopify.analytics. The old code checked
             window.analytics, which is undefined on this store, so this block
             never ran and AddToCart never reached any Shopify-channel pixel. */
          try {
            var sa = (window.Shopify && window.Shopify.analytics) || window.analytics;
            if (sa && typeof sa.publish === 'function' && addedItem) {
              sa.publish('product_added_to_cart', {
                cartLine: {
                  cost: {
                    totalAmount: {
                      amount: addedItem.final_line_price / 100,
                      currencyCode: (cart && cart.currency) || 'NZD'
                    }
                  },
                  merchandise: {
                    id: 'gid://shopify/ProductVariant/' + addedItem.variant_id,
                    title: addedItem.variant_title || '',
                    sku: addedItem.sku || '',
                    price: { amount: addedItem.final_price / 100, currencyCode: (cart && cart.currency) || 'NZD' },
                    product: {
                      id: 'gid://shopify/Product/' + addedItem.product_id,
                      title: addedItem.product_title,
                      type: addedItem.product_type || '',
                      url: addedItem.url || ''
                    }
                  },
                  quantity: addedItem.quantity
                }
              });
            }
          } catch (e) { console.warn('KYRO PDP: Shopify pixel failed', e); }

          /* 2b. META PIXEL — fire AddToCart directly.
             Deterministic: does not rely on Shopify's web-pixel bridge picking
             up a custom AJAX add. fbq is already on the page via the Meta
             channel app. If Events Manager shows AddToCart twice after this
             deploy, delete this block and keep 2a. */
          try {
            if (typeof window.fbq === 'function' && addedItem) {
              window.fbq('track', 'AddToCart', {
                content_ids: [String(addedItem.product_id)],
                content_name: addedItem.product_title,
                content_type: 'product',
                contents: [{ id: String(addedItem.variant_id), quantity: addedItem.quantity }],
                value: addedItem.final_line_price / 100,
                currency: (cart && cart.currency) || 'NZD'
              });
            }
          } catch (e) { console.warn('KYRO PDP: Meta pixel failed', e); }

          /* 3. GENERIC THEME EVENTS — Impact + most app integrations.
             Fired on both document and window so any listener catches them. */
          var eventNames = ['cart:updated', 'cart:item-added', 'product:added-to-cart', 'theme:cart:add', 'theme:cart:open'];
          eventNames.forEach(function (name) {
            try {
              document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
              window.dispatchEvent(new CustomEvent(name, { detail: detail }));
            } catch (e) { /* ignore */ }
          });
        });
    }

    /* ---- STICKY BUY ---- */
    var stickyBuy = root.querySelector('[data-sticky-buy]');
    var buybox = root.querySelector('.buybox');
    function stickyBuyVisibility() {
      if (!stickyBuy || !buybox) return;
      var r = buybox.getBoundingClientRect();
      var show = r.bottom < 120 && (window.innerHeight - r.bottom) > 100;
      stickyBuy.classList.toggle('show', show);
    }
    window.addEventListener('scroll', stickyBuyVisibility, { passive: true });
    var sbAtc = root.querySelector('[data-sb-atc]');
    if (sbAtc && form) sbAtc.addEventListener('click', function () { form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); });
    var ctaBuy = root.querySelector('[data-cta-buy]');
    if (ctaBuy && form) ctaBuy.addEventListener('click', function () { form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); });

    /* ---- MARQUEE: duplication now happens in Liquid (kyro-value-strip.liquid) ---- */

    /* ---- REVEAL on scroll ---- */
    var reveals = $all('.reveal', root);
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      reveals.forEach(function (el) { io.observe(el); });
    } else {
      reveals.forEach(function (el) { el.classList.add('in'); });
    }

    /* ---- INIT ---- */
    refreshSubscribePmodeForVariant();
    syncPmodeFromDom();
    updatePrice();
    stickyBuyVisibility();
  }
})();
